import React from 'react';
import { MapPin, Clock, Phone, Mail, CheckCircle2, Trophy } from 'lucide-react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import SEOHelmet from '../../components/SEOHelmet';
import { companyInfo } from '../../mockData';

const LaQuintaPage = () => {
  return (
    <div style={{ backgroundColor: 'var(--bg-page)', minHeight: '100vh' }}>
      <SEOHelmet 
        title="Process Server La Quinta CA | Legal Document Services | Legal Document Specialist"
        description="La Quinta luxury resort process server. Discreet service for PGA West, Hideaway Golf Club, and upscale residential communities. Specialized in high-net-worth client confidential document service."
        keywords="process server La Quinta CA, legal document services La Quinta, court filing La Quinta, skip tracing La Quinta, Riverside County process server"
        canonical="https://legaldocspecialist.com/cities/la-quinta"
      />
      <Header />

      {/* Hero Section */}
      <section style={{ backgroundColor: 'var(--bg-page)', padding: '160px 0 80px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', maxWidth: '800px', margin: '0 auto' }}>
            <h1 className="brand-display" style={{
              marginBottom: '1.5rem',
              color: 'var(--text-primary)'
            }}>
              Process Server La Quinta, California
            </h1>
            <p className="body-large" style={{
              color: 'var(--text-secondary)',
              marginBottom: '2rem',
              lineHeight: '1.6'
            }}>
              Premium legal document services in La Quinta, CA. Professional, registered and bonded process servers specializing in luxury resort, golf community, and high-end residential legal services.
            </p>

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/place-order" className="btn-primary" style={{ textDecoration: 'none' }}>
                Place Order Now
              </Link>
              <Link to="/quote" className="btn-secondary" style={{ textDecoration: 'none' }}>
                Get Free Quote
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services & Luxury Focus */}
      <section style={{ backgroundColor: 'var(--bg-card)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
              La Quinta Legal Document Services
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '600px', margin: '0 auto' }}>
              Specialized legal support services for La Quinta's luxury resort community, golf courses, and upscale residential areas.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem' }}>
            {/* Services */}
            <div style={{
              backgroundColor: 'var(--bg-page)',
              padding: '2rem',
              borderRadius: '12px',
              border: '1px solid var(--border-medium)'
            }}>
              <h3 className="heading-3" style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center' }}>
                <CheckCircle2 size={24} color="var(--brand-primary)" style={{ marginRight: '0.5rem' }} />
                Premium Services
              </h3>
              <ul style={{ listStyle: 'none', padding: 0 }}>
                {[
                  'Process Serving & Legal Notices',
                  'Court Document Filing',
                  'County Recording Services',
                  'Document Retrieval & Research',
                  'Skip Tracing & Locate Services'
                ].map((service, index) => (
                  <li key={service} style={{ 
                    padding: '0.75rem 0',
                    borderBottom: index < 4 ? '1px solid var(--border-light)' : 'none',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    <CheckCircle2 size={16} color="var(--brand-primary)" style={{ marginRight: '0.75rem' }} />
                    <span style={{ color: 'var(--text-primary)' }}>{service}</span>
                  </li>
                ))}
              </ul>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginTop: '1rem' }}>
                Specialized service for luxury resort communities and high-end residential legal matters.
              </p>
            </div>

            {/* La Quinta Specialties */}
            <div style={{
              backgroundColor: 'var(--bg-page)',
              padding: '2rem',
              borderRadius: '12px',
              border: '1px solid var(--border-medium)'
            }}>
              <h3 className="heading-3" style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center' }}>
                <Trophy size={24} color="var(--brand-primary)" style={{ marginRight: '0.5rem' }} />
                La Quinta Expertise
              </h3>
              <div style={{ color: 'var(--text-primary)', lineHeight: '1.6' }}>
                <p style={{ marginBottom: '1rem' }}>
                  <strong>Golf Course Communities</strong><br />
                  <span style={{ color: 'var(--text-secondary)' }}>Specialized service for PGA West and resort areas</span>
                </p>
                <p style={{ marginBottom: '1rem' }}>
                  <strong>Luxury Resort Experience</strong><br />
                  <span style={{ color: 'var(--text-secondary)' }}>Professional service for high-end hospitality sector</span>
                </p>
                <p style={{ marginBottom: '1rem' }}>
                  <strong>Gated Community Access</strong><br />
                  <span style={{ color: 'var(--text-secondary)' }}>Experienced with exclusive residential areas</span>
                </p>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', fontStyle: 'italic' }}>
                  Discreet, professional service tailored to La Quinta's upscale community standards.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Excellence */}
      <section style={{ backgroundColor: 'var(--bg-page)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
              Why Choose Us for La Quinta?
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '700px', margin: '0 auto' }}>
              Premium service standards meet local expertise. We understand La Quinta's unique luxury community environment and service expectations.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '2rem' }}>
            {[
              {
                icon: <Trophy size={32} />,
                title: 'Luxury Market Experience',
                description: 'Specialized experience serving high-end residential communities and luxury resort properties.'
              },
              {
                icon: <Clock size={32} />,
                title: 'Flexible Scheduling',
                description: 'All service levels available with discreet, professional delivery tailored to your timeline.'
              },
              {
                icon: <CheckCircle2 size={32} />,
                title: 'Premium Standards',
                description: 'Registered, bonded, and insured with the highest professional standards and discretion.'
              }
            ].map((feature) => (
              <div key={feature.title} style={{
                backgroundColor: 'var(--bg-card)',
                padding: '2rem',
                borderRadius: '12px',
                border: '1px solid var(--border-medium)',
                textAlign: 'center'
              }}>
                <div style={{ color: 'var(--brand-primary)', marginBottom: '1rem', display: 'flex', justifyContent: 'center' }}>
                  {feature.icon}
                </div>
                <h3 className="heading-3" style={{ marginBottom: '1rem' }}>{feature.title}</h3>
                <p className="body-text" style={{ color: 'var(--text-secondary)' }}>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Area Coverage */}
      <section style={{ backgroundColor: 'var(--bg-card)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
              La Quinta Service Coverage
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '600px', margin: '0 auto' }}>
              Serving La Quinta and surrounding luxury Coachella Valley communities with premium legal document services.
            </p>
          </div>

          <div style={{
            backgroundColor: 'var(--bg-page)',
            padding: '2rem',
            borderRadius: '12px',
            border: '1px solid var(--border-medium)',
            textAlign: 'center'
          }}>
            <h3 className="heading-3" style={{ marginBottom: '1.5rem' }}>
              Premium Communities Served from La Quinta
            </h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: '1rem',
              marginBottom: '2rem'
            }}>
              {[
                'La Quinta', 'Indian Wells', 'Palm Desert', 'Rancho Mirage',
                'Bermuda Dunes', 'Indio', 'Coachella', 'Palm Springs',
                'PGA West', 'The Hideaway', 'Coral Mountain', 'Desert Willow'
              ].map((city, index) => (
              <div key={city} style={{
                  padding: '0.75rem',
                  backgroundColor: index === 0 ? 'var(--brand-primary)' : 'var(--bg-card)',
                  borderRadius: '6px',
                  border: '1px solid var(--border-light)'
                }}>
                  <span style={{ 
                    color: index === 0 ? 'white' : 'var(--text-primary)',
                    fontWeight: index === 0 ? '600' : 'normal'
                  }}>{city}</span>
                </div>
              ))}
            </div>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
              Comprehensive coverage of central Coachella Valley luxury communities and resort areas.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" style={{ backgroundColor: 'var(--bg-page)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
              Contact Us for La Quinta Services
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '600px', margin: '0 auto' }}>
              Ready to get started? Contact Legal Document Specialist for premium, discreet service in La Quinta.
            </p>
          </div>

          <div style={{
            maxWidth: '600px',
            margin: '0 auto',
            backgroundColor: 'var(--bg-card)',
            padding: '2rem',
            borderRadius: '12px',
            border: '1px solid var(--border-medium)',
            textAlign: 'center'
          }}>
            <div style={{ marginBottom: '2rem' }}>
              <Phone size={24} color="var(--brand-primary)" style={{ marginBottom: '0.5rem' }} />
              <div style={{ fontWeight: '600', color: 'var(--text-primary)', fontSize: '1.2rem' }}>{companyInfo.phone}</div>
              <div style={{ color: 'var(--text-secondary)' }}>Monday-Friday 9AM-5PM PST</div>
            </div>

            <div style={{ marginBottom: '2rem' }}>
              <Mail size={24} color="var(--brand-primary)" style={{ marginBottom: '0.5rem' }} />
              <div style={{ fontWeight: '600', color: 'var(--text-primary)' }}>{companyInfo.email}</div>
              <div style={{ color: 'var(--text-secondary)' }}>Confidential communication guaranteed</div>
            </div>

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap', marginBottom: '2rem' }}>
              <Link to="/place-order" className="btn-primary" style={{ textDecoration: 'none' }}>
                Place Order Online
              </Link>
              <Link to="/quote" className="btn-secondary" style={{ textDecoration: 'none' }}>
                Request Quote
              </Link>
            </div>

            <div style={{ 
              backgroundColor: 'var(--bg-page)', 
              padding: '1rem', 
              borderRadius: '6px',
              border: '1px solid var(--border-light)'
            }}>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', margin: 0 }}>
                <strong>Serving La Quinta's Luxury Community</strong><br />
                Golf Course Specialists • Resort Experience • Discreet & Professional Service
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LaQuintaPage;